import { useState, useEffect } from 'react';
import {
  Shield,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  User,
  Database,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';

interface AuditLogEntry {
  id: string;
  ts: string;
  adminUserId: string;
  adminEmail: string | null;
  adminName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  httpMethod: string | null;
  httpPath: string | null;
  requestIp: string | null;
  userAgent: string | null;
  status: string;
  durationMs: number | null;
  impactCount: number | null;
  errorMessage: string | null;
  payloadSummary: string | null;
  tags: string | null;
  environment: string | null;
  serviceVersion: string | null;
}

interface PageResponse {
  content: AuditLogEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const ACTION_TYPES = [
  'SQL_EXECUTE',
  'USER_SUSPEND',
  'USER_UNSUSPEND',
  'SQUAD_FREEZE',
  'MODERATION_RESOLVE',
  'MODERATION_DISMISS',
  'CHAT_FLAG_REVIEW',
  'CRUD_CREATE',
  'CRUD_UPDATE',
  'CRUD_DELETE',
  'COMPLIANCE_EXPORT',
  'DDB_QUERY',
];

const STATUS_TYPES = ['SUCCESS', 'FAIL', 'ERROR'];

export function AuditLogPage() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  // Filters
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterAdminUserId, setFilterAdminUserId] = useState<string>('');
  const [filterResourceType, setFilterResourceType] = useState<string>('');

  // Selected log for detail view
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  useEffect(() => {
    fetchLogs();
  }, [page, filterAction, filterStatus, filterAdminUserId, filterResourceType]);

  const fetchLogs = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      });

      if (filterAction) params.append('action', filterAction);
      if (filterStatus) params.append('status', filterStatus);
      if (filterAdminUserId) params.append('adminUserId', filterAdminUserId);
      if (filterResourceType) params.append('resourceType', filterResourceType);

      const response = await fetch(`/api/v1/admin/audit?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data: PageResponse = await response.json();
      setLogs(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterStatus('');
    setFilterAdminUserId('');
    setFilterResourceType('');
    setPage(0);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'FAIL':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case 'ERROR':
        return <AlertCircle className="h-4 w-4 text-amber-400" />;
      default:
        return <AlertCircle className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'px-2 py-0.5 rounded text-xs font-medium';
    switch (status) {
      case 'SUCCESS':
        return `${baseClasses} bg-green-900/50 text-green-400`;
      case 'FAIL':
        return `${baseClasses} bg-red-900/50 text-red-400`;
      case 'ERROR':
        return `${baseClasses} bg-amber-900/50 text-amber-400`;
      default:
        return `${baseClasses} bg-slate-800 text-slate-400`;
    }
  };

  const formatTimestamp = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleString();
  };

  const formatRelativeTime = (ts: string) => {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const hasActiveFilters = filterAction || filterStatus || filterAdminUserId || filterResourceType;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="h-7 w-7 text-cyan-400" />
            Audit Log
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            View all admin actions and their outcomes
          </p>
        </div>
        <div className="text-sm text-slate-400">
          {totalElements} total entries
        </div>
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-3 mb-4 p-4 bg-slate-900/50 rounded-lg border border-slate-800">
        <Filter className="h-4 w-4 text-slate-400" />

        <select
          value={filterAction}
          onChange={(e) => {
            setFilterAction(e.target.value);
            setPage(0);
          }}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="">All Actions</option>
          {ACTION_TYPES.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(0);
          }}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="">All Status</option>
          {STATUS_TYPES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Admin User ID"
          value={filterAdminUserId}
          onChange={(e) => {
            setFilterAdminUserId(e.target.value);
            setPage(0);
          }}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 w-40"
        />

        <input
          type="text"
          placeholder="Resource Type"
          value={filterResourceType}
          onChange={(e) => {
            setFilterResourceType(e.target.value);
            setPage(0);
          }}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 w-32"
        />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded text-sm text-slate-300 flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 flex gap-4">
        {/* Log list */}
        <div className="flex-1 min-w-0 flex flex-col bg-slate-900/30 rounded-lg border border-slate-800">
          {/* Table */}
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full text-red-400">
                <AlertCircle className="h-5 w-5 mr-2" />
                {error}
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <Shield className="h-12 w-12 mb-3 opacity-50" />
                <p>No audit logs found</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-800 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 bg-slate-800">
                      Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 bg-slate-800">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 bg-slate-800">
                      Admin
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 bg-slate-800">
                      Resource
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 bg-slate-800">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 bg-slate-800">
                      Duration
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className={`hover:bg-slate-800/50 cursor-pointer ${
                        selectedLog?.id === log.id ? 'bg-slate-800/70' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-slate-500" />
                          <div>
                            <div className="text-slate-300 text-xs">
                              {formatRelativeTime(log.ts)}
                            </div>
                            <div className="text-slate-500 text-xs">
                              {formatTimestamp(log.ts)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-cyan-400 text-xs">
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="h-3.5 w-3.5 text-slate-500" />
                          <span className="text-slate-300 text-xs truncate max-w-[150px]">
                            {log.adminEmail || log.adminUserId}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {log.resourceType && (
                          <div className="flex items-center gap-1">
                            <Database className="h-3.5 w-3.5 text-slate-500" />
                            <span className="text-slate-400 text-xs">
                              {log.resourceType}
                              {log.resourceId && `:${log.resourceId}`}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={getStatusBadge(log.status)}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {log.durationMs != null ? `${log.durationMs}ms` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-slate-800">
              <div className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-1 rounded hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 text-slate-400" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedLog && (
          <div className="w-96 flex-shrink-0 bg-slate-900/50 rounded-lg border border-slate-800 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <h3 className="text-sm font-medium text-white">Audit Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1 rounded hover:bg-slate-800 text-slate-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Status */}
              <div className="flex items-center gap-3">
                {getStatusIcon(selectedLog.status)}
                <span className={getStatusBadge(selectedLog.status)}>
                  {selectedLog.status}
                </span>
                {selectedLog.durationMs != null && (
                  <span className="text-xs text-slate-500">
                    {selectedLog.durationMs}ms
                  </span>
                )}
              </div>

              {/* Action */}
              <div>
                <div className="text-xs text-slate-500 mb-1">Action</div>
                <div className="font-mono text-cyan-400">{selectedLog.action}</div>
              </div>

              {/* Timestamp */}
              <div>
                <div className="text-xs text-slate-500 mb-1">Timestamp</div>
                <div className="text-slate-300">{formatTimestamp(selectedLog.ts)}</div>
              </div>

              {/* Admin */}
              <div>
                <div className="text-xs text-slate-500 mb-1">Admin</div>
                <div className="text-slate-300">
                  {selectedLog.adminName || selectedLog.adminEmail || selectedLog.adminUserId}
                </div>
                {selectedLog.adminEmail && (
                  <div className="text-xs text-slate-500">{selectedLog.adminEmail}</div>
                )}
              </div>

              {/* Resource */}
              {selectedLog.resourceType && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Resource</div>
                  <div className="text-slate-300">
                    {selectedLog.resourceType}
                    {selectedLog.resourceId && ` / ${selectedLog.resourceId}`}
                  </div>
                </div>
              )}

              {/* HTTP Info */}
              {selectedLog.httpMethod && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">HTTP Request</div>
                  <div className="text-slate-300 font-mono text-xs">
                    {selectedLog.httpMethod} {selectedLog.httpPath}
                  </div>
                </div>
              )}

              {/* Client Info */}
              {selectedLog.requestIp && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Client IP</div>
                  <div className="text-slate-300 font-mono text-xs">{selectedLog.requestIp}</div>
                </div>
              )}

              {/* Error Message */}
              {selectedLog.errorMessage && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Error</div>
                  <div className="text-red-400 text-sm bg-red-900/20 p-2 rounded border border-red-800/50">
                    {selectedLog.errorMessage}
                  </div>
                </div>
              )}

              {/* Payload Summary */}
              {selectedLog.payloadSummary && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Payload Summary</div>
                  <pre className="text-slate-300 text-xs bg-slate-800 p-2 rounded overflow-x-auto">
                    {selectedLog.payloadSummary}
                  </pre>
                </div>
              )}

              {/* Tags */}
              {selectedLog.tags && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Tags</div>
                  <div className="flex flex-wrap gap-1">
                    {JSON.parse(selectedLog.tags).map((tag: string) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Environment */}
              <div>
                <div className="text-xs text-slate-500 mb-1">Environment</div>
                <div className="text-slate-300">
                  {selectedLog.environment || 'Unknown'}
                  {selectedLog.serviceVersion && ` (v${selectedLog.serviceVersion})`}
                </div>
              </div>

              {/* ID */}
              <div>
                <div className="text-xs text-slate-500 mb-1">Audit ID</div>
                <div className="text-slate-400 font-mono text-xs break-all">{selectedLog.id}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
